import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Polyline, Popup, useMap } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';
import { AppContext } from '../../../context/context';
import { useHighlight } from '../../../context/HighlightContext';
import { isOsmRouteLine, getOsmRouteDefaultColor } from '../../../types/osmRoutes';
import type { Line } from '../../../context/context';

/**
 * Max number of OSM transit-route polylines rendered at once. Routes outside
 * this cap are still in state and can still be highlighted from the Layer
 * Panel; the cap exists to keep map interaction fluid when an analysis
 * polygon pulls in 1000+ routes from OSM (e.g. Sydney CBD).
 */
const OSM_ROUTE_RENDER_CAP = 250;

/**
 * Memoized popup body. React-leaflet keeps Popup children in the React tree
 * even when the popup is closed, so memoization stops every parent re-render
 * (e.g. on map pan) from rebuilding hundreds of popup JSX trees.
 */
const OsmRoutePopupContent = React.memo(function OsmRoutePopupContent({
    meta,
}: {
    meta: import('../../../types/osmRoutes').OsmRouteMetadata;
}) {
    const swatch = meta.colour || getOsmRouteDefaultColor(meta.mode);
    return (
        <div style={{ minWidth: 240, fontFamily: 'inherit' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, backgroundColor: swatch, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                <strong style={{ fontSize: 14 }}>
                    {meta.ref ? `${meta.ref} — ${meta.routeName}` : meta.routeName}
                </strong>
            </div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#666', marginBottom: 8 }}>
                {meta.mode.replace('_', ' ')}{meta.network ? ` · ${meta.network}` : ''}
            </div>
            <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                    {meta.operator && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Operator</td><td>{meta.operator}</td></tr>)}
                    {meta.from && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>From</td><td>{meta.from}</td></tr>)}
                    {meta.to && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>To</td><td>{meta.to}</td></tr>)}
                    {meta.via && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Via</td><td>{meta.via}</td></tr>)}
                    {meta.interval && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Interval</td><td>{meta.interval} min</td></tr>)}
                    {meta.openingHours && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Hours</td><td>{meta.openingHours}</td></tr>)}
                    {meta.duration && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Duration</td><td>{meta.duration}</td></tr>)}
                    {meta.stopCount > 0 && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Stops</td><td>{meta.stopCount}</td></tr>)}
                    {meta.wheelchair && meta.wheelchair !== 'unknown' && (<tr><td style={{ color: '#666', paddingRight: 8, verticalAlign: 'top' }}>Wheelchair</td><td>{meta.wheelchair}</td></tr>)}
                </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>
                Data from OpenStreetMap (OSM ID {meta.osmRouteId})
            </div>
        </div>
    );
});

/**
 * Renders all OSM transit-route lines on a shared canvas with viewport
 * culling. Mirrors the pattern in TripLayerRenderer: one <canvas> instead of
 * N SVG paths, off-screen routes skipped, selected routes always rendered.
 *
 * Non-OSM-route lines (fixed routes, hand-drawn, trip lines) are handled
 * by their existing renderers and intentionally excluded here.
 */
const OsmRouteLayerRenderer: React.FC = () => {
    const context = useContext(AppContext);
    const map = useMap();

    const canvasRenderer = useRef(L.canvas({ padding: 0.5 }));

    const [viewportBounds, setViewportBounds] = React.useState<LatLngBounds>(
        () => map.getBounds()
    );

    useEffect(() => {
        const onViewportChange = () => setViewportBounds(map.getBounds());
        map.on('moveend', onViewportChange);
        map.on('zoomend', onViewportChange);
        return () => {
            map.off('moveend', onViewportChange);
            map.off('zoomend', onViewportChange);
        };
    }, [map]);

    const { highlightedIds, setHighlighted } = useHighlight();
    const setHighlightedRef = useRef(setHighlighted);
    useEffect(() => { setHighlightedRef.current = setHighlighted; }, [setHighlighted]);

    const mapLayers = context?.global.mapLayers ?? [];

    const elements = useMemo(() => {
        const padded = viewportBounds.pad(0.15);

        // Pull all OSM route lines from all visible layers, with a stable
        // (highlighted-first) order so the render cap never drops a highlighted route.
        const routes: Array<{ line: Line; layer: typeof mapLayers[number] }> = [];
        for (const layer of mapLayers) {
            if (layer.hidden) continue;
            for (const shape of layer.shapes) {
                if (shape.type !== 'line' || shape.hidden) continue;
                if (!isOsmRouteLine(shape)) continue;
                routes.push({ line: shape, layer });
            }
        }
        routes.sort((a, b) => {
            const aSel = highlightedIds.has(a.line.id);
            const bSel = highlightedIds.has(b.line.id);
            return aSel === bSel ? 0 : aSel ? -1 : 1;
        });

        const result: React.ReactElement[] = [];
        let rendered = 0;

        for (const { line } of routes) {
            const isSelected = highlightedIds.has(line.id);
            const positions: [number, number][] = line.coordinates
                .filter(c =>
                    Array.isArray(c) &&
                    c.length >= 2 &&
                    typeof c[0] === 'number' &&
                    typeof c[1] === 'number' &&
                    isFinite(c[0]) &&
                    isFinite(c[1])
                )
                .map(c => [c[0], c[1]] as [number, number]);
            if (positions.length < 2) continue;

            // Viewport cull. Selected routes are always rendered.
            if (!isSelected) {
                try {
                    const shapeBounds = L.latLngBounds(positions.map(p => L.latLng(p[0], p[1])));
                    if (!padded.intersects(shapeBounds)) continue;
                } catch {
                    continue;
                }
                if (rendered >= OSM_ROUTE_RENDER_CAP) continue;
            }
            rendered += 1;

            const meta = line.osmRouteMetadata!;
            const colour = line.colour || meta.colour || getOsmRouteDefaultColor(meta.mode);
            const baseWeight = 4;
            const weight = isSelected ? baseWeight + 4 : baseWeight;
            const opacity = isSelected ? 1 : 0.85;

            result.push(
                <Polyline
                    key={line.id}
                    positions={positions}
                    pathOptions={{
                        color: colour,
                        weight,
                        opacity,
                        renderer: canvasRenderer.current,
                    }}
                    eventHandlers={{
                        click: () => {
                            // Highlight the route + its associated stops via the
                            // dedicated HighlightContext — separate from
                            // selectedShapes so we don't cascade re-renders into
                            // the Layer Panel and other selection consumers.
                            setHighlightedRef.current([line.id, ...(meta.stopShapeIds ?? [])]);
                        },
                    }}
                >
                    <Popup>
                        <OsmRoutePopupContent meta={meta} />
                    </Popup>
                </Polyline>
            );
        }

        return result;
    }, [mapLayers, highlightedIds, viewportBounds]);

    return <>{elements}</>;
};

export default OsmRouteLayerRenderer;
